/**
 * XC-AI-013 — HR AI bias opt-in modal.
 *
 * Shown the first time the user requests an AI HR tool while
 * `user.ai_hr_optin` is false (or after they reject it). Surfaces the
 * EU AI Act Anexo III.4 disclaimer; once accepted persists via
 * PUT /api/auth/me/ai-hr-optin and updates the session in AuthContext.
 *
 * Triggers:
 *   - Backend tool result with `code: 'AI_HR_OPTIN_REQUIRED'` (preferred)
 *   - Frontend pre-flight when user opens an HR-AI panel
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/api/apiFetch';
import { toastApiError } from '@/lib/query-client';
import { toast } from 'sonner';

export default function HrToolOptIn({ open, onClose, onAccepted }) {
  const { t } = useTranslation();
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  if (!open) return null;

  const handleAccept = async () => {
    if (!accepted || accepting) return;
    setAccepting(true);
    try {
      await apiFetch('/api/auth/me/ai-hr-optin', {
        method: 'PUT',
        body: JSON.stringify({ accepted: true }),
      });
      toast.success(t('hr.optin_accepted'));
      onAccepted?.();
      onClose?.();
    } catch (error) {
      toastApiError(error, t('hr.optin_save_error'));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 lg-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hr-optin-title"
    >
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 lg-glass lg-modal">
        <h2 id="hr-optin-title" className="text-lg font-semibold mb-4">
          Aviso obligatorio: IA aplicada a datos de RRHH
        </h2>
        <div className="space-y-3 text-sm text-foreground mb-4">
          <p>
            VERIX usa modelos de lenguaje (Claude Sonnet 4.6 vía AWS Bedrock) para
            asistirte en consultas y operaciones sobre empleados, nóminas, ausencias
            y contratos. El Reglamento Europeo 2024/1689 (Ley de IA) clasifica estos
            casos de uso como <strong>alto riesgo (Anexo III.4)</strong>.
          </p>
          <p>
            Los modelos de IA pueden contener sesgos derivados de su entrenamiento.
            Por ello:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>El humano decide.</strong> Toda recomendación de IA sobre RRHH
              debe ser revisada antes de aplicarla.
            </li>
            <li>
              <strong>No tomes decisiones automáticas</strong> sobre contratación,
              despido, evaluación de rendimiento o retribución basándote solo en
              salida de IA.
            </li>
            <li>
              <strong>Verifica siempre</strong> nóminas, fechas de contrato y
              ausencias contra los datos oficiales de la persona.
            </li>
            <li>
              <strong>Tienes derecho a impugnar</strong> cualquier decisión asistida
              por IA (RGPD Art. 22).
            </li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Las acciones que ejecutes seguirán requiriendo confirmación explícita.
            Puedes retirar este consentimiento en cualquier momento desde Configuración › Mi perfil.
          </p>
        </div>

        <label className="flex items-start gap-2 mb-5 text-sm">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1"
            data-testid="hr-optin-checkbox"
          />
          <span>
            He leído el aviso y entiendo que la IA es una herramienta de apoyo,
            no de decisión automática.
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={accepting}
            className="px-4 py-2 border rounded text-sm hover:bg-muted disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={!accepted || accepting}
            data-testid="hr-optin-accept"
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {accepting ? 'Guardando…' : 'Aceptar y continuar'}
          </button>
        </div>
      </div>
    </div>
  );
}
